import { useEffect, useState, type KeyboardEvent, type RefObject } from 'react';
import {
    initialListboxActiveIndex,
    isListboxNavigationKey,
    nextListboxActiveIndex,
} from '@/shared/utils/listboxActiveIndex';

interface UseSelectKeyboardParams<T> {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    options: readonly T[];
    selectedIndex: number;
    canInteract: boolean;
    onSelect: (option: T) => void;
    triggerRef: RefObject<HTMLElement | null>;
    optionId: (index: number) => string;
    isMenuMounted: boolean;
}

export function useSelectKeyboard<T>({
    isOpen,
    setIsOpen,
    options,
    selectedIndex,
    canInteract,
    onSelect,
    triggerRef,
    optionId,
    isMenuMounted,
}: UseSelectKeyboardParams<T>) {
    const [storedActiveIndex, setActiveIndex] = useState(-1);
    const activeIndex = Math.min(storedActiveIndex, options.length - 1);
    const activeOptionId = activeIndex >= 0 ? optionId(activeIndex) : null;

    useEffect(() => {
        if (!isMenuMounted || !activeOptionId) return;
        document.getElementById(activeOptionId)?.scrollIntoView?.({ block: 'nearest' });
    }, [isMenuMounted, activeOptionId]);

    const openMenu = () => {
        setActiveIndex(initialListboxActiveIndex(selectedIndex, options.length));
        setIsOpen(true);
    };

    const toggleMenu = () => {
        if (!canInteract) return;
        if (isOpen) {
            setIsOpen(false);
        } else {
            openMenu();
        }
    };

    const moveActive = (key: Parameters<typeof nextListboxActiveIndex>[1]) => {
        setActiveIndex(nextListboxActiveIndex(activeIndex, key, options.length));
    };

    const closeAndKeepFocus = () => {
        setIsOpen(false);
        triggerRef.current?.focus();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (!canInteract) return;
        const { key } = event;

        if (isListboxNavigationKey(key)) {
            if (isOpen) {
                event.preventDefault();
                moveActive(key);
            } else if (key === 'ArrowDown' || key === 'ArrowUp') {
                event.preventDefault();
                openMenu();
            }
            return;
        }

        if (key === 'Enter' || key === ' ') {
            event.preventDefault();
            if (isOpen && activeIndex >= 0) {
                onSelect(options[activeIndex]);
                return;
            }
            toggleMenu();
            return;
        }

        if (!isOpen) return;
        if (key === 'Escape') {
            // Stops a parent Modal's document-level Escape handler from also closing the dialog.
            event.preventDefault();
            event.stopPropagation();
            closeAndKeepFocus();
        } else if (key === 'Tab') {
            setIsOpen(false);
        }
    };

    return { activeIndex, setActiveIndex, toggleMenu, handleKeyDown };
}
